<script setup lang="ts">
  import { computed } from 'vue';
  import { ApplicationError } from '@/Services/Clients/applicationError';
  import ModalDialog from '@/VueComponents/Modals/ModalDialog.vue';
  import logger from '@/Utils/logger';
  import resourceHelper from '@/Utils/resourceHelper';
  import ReportService from '@/Services/reportService';
  import reportDownloader from '@/Utils/reportDownloader';
  import { CreateReportModalTypes } from '@/Types/Enums/createReportModalTypes';
  import TextInputField from '@/VueComponents/SharedComponents/InputFields/TextInputField.vue';
  import { useForm } from 'vee-validate';
  import useResetableState from '@/VueCore/utils/useResetableState';

  const props = defineProps<{
    reportItemReferenceNumber: string | null,
    reportItemId: string | null,
    itemType: CreateReportModalTypes
  }>();

  const visible = defineModel<boolean>({ default: false });

  const modalTitle = computed(() => {
    if (props.itemType === CreateReportModalTypes.request) {
      return resourceHelper.getString('CreateRequestReportModalTitle');
    }
    if (props.itemType === CreateReportModalTypes.uploadFolder) {
      return resourceHelper.getString('CreateUploadFolderReportModalTitle');
    }
    return '';
  });

  const { state, resetState } = useResetableState({
    reportTitle: reportDownloader.getDefaultReportTitle(props.reportItemReferenceNumber),
    isCreatingReport: false
  });

  const form = useForm({
    initialValues: {
      reportTitle: state.reportTitle
    }
  });

  async function onModalCreateReport() {
    const validationResult = await form.validate();
    if (validationResult.valid) {
      await createReport(state.reportTitle, props.reportItemId);
    }
  }

  async function createReport(reportTitle: string, reportItemId: string) {
    switch (props.itemType) {
    case CreateReportModalTypes.request:
      await createRequestReport(reportTitle, reportItemId);
      break;
    case CreateReportModalTypes.uploadFolder:
      await createUploadFolderReport(reportTitle, reportItemId);
      break;
    }
  }

  function createUploadFolderReport(reportTitle: string, reportItemId: string) {
    state.isCreatingReport = true;
    ReportService.getUploadFolderReport(getReportParams(reportTitle, reportItemId))
      .then(data => {
        reportDownloader.downloadReport(data, reportTitle);
        logger.success('UploadFolderReportCreated');
      })
      .catch((error: ApplicationError) => {
        if (error.errorData?.errorMessages?.length) {
          logger.untranslatedWarning(error.errorData.errorMessages[0]);
        } else {
          logger.error('UploadFolderReportFailed');
        }
      })
      .finally(() => {
        state.isCreatingReport = false;
        visible.value = false;
      });
  }

  async function createRequestReport(reportTitle: string, reportItemId: string) {
    state.isCreatingReport = true;

    ReportService.getRequestReport(getReportParams(reportTitle, reportItemId))
      .then(data => {
        reportDownloader.downloadReport(data, reportTitle);
        logger.success('RequestReportCreated');
      })
      .catch((error: ApplicationError) => {
        if (error.errorData?.errorMessages?.length) {
          logger.untranslatedWarning(error.errorData.errorMessages[0]);
        } else {
          logger.error('FailedToGenerateRequestReport', null, error);
        }
      })
      .finally(() => {
        state.isCreatingReport = false;
        visible.value = false;
      });
  }

  function getReportParams(reportTitle: string, itemId: string) {
    const result: any = {
      title: reportTitle,
      clientTimezoneOffset: new Date().getTimezoneOffset().toString(),
      clientTimezoneName: new Date().toTimeString().slice(9)
    };

    if (props.itemType === CreateReportModalTypes.request) {
      result.requestId = itemId;
    } else if (props.itemType === CreateReportModalTypes.uploadFolder) {
      result.selfResponseId = itemId;
    }

    return result;
  }

  function onOpen() {
    resetState();
    state.reportTitle = reportDownloader.getDefaultReportTitle(props.reportItemReferenceNumber);
  }
</script>

<template>
  <ModalDialog
    v-model="visible"
    :title="modalTitle"
    @open="onOpen"
  >
    <template #default>
      <div>
        <TextInputField
          id="reportTitleInput"
          v-model="state.reportTitle"
          maxlength="200"
          :label="$localize('Title')"
          :validation-rules="{ required: { message: $localize('TitleRequired') } }"
        />
        <div
          class="py-3"
          data-test="request-report-modal-text"
        >
          {{ $localize('CreateReportModalText') }}
        </div>
      </div>
    </template>
    <template #footer>
      <button
        class="btn btn-outline-secondary"
        data-test="request-report-cancel-button"
        @click="visible = false"
      >
        {{ $localize('Cancel') }}
      </button>
      <button
        class="btn btn-primary"
        :disabled="state.isCreatingReport"
        data-test="request-report-download-button"
        @click="onModalCreateReport"
      >
        <span
          v-if="state.isCreatingReport"
          class="fa-regular fa-circle-notch fa-spin me-2"
          aria-hidden="true"
        />
        <span>
          {{ $localize('Download') }}
        </span>
      </button>
    </template>
  </ModalDialog>
</template>
